import { loadErrorMessages, loadDevMessages } from "@apollo/client/dev";

import categoryInfoService from "../../services/categoryInfo";
import cartService, { getCartId, setCartId } from "../../services/cartService";
import packageService from "../../services/packageService";
import {
  AddBundleItemState,
  AddVirtualItemState,
  CartState,
} from "../../utils/interfaces/cart";
import {
  CustomizableDropDownOption,
  ItemState,
  PackageState,
} from "../../utils/interfaces/packages";
import {
  CategoryItemState,
  CategoryState,
} from "../../utils/interfaces/categoryInfo";
import { addDataLayer, generateSecureRandomId } from "../../utils/utility";
import { productKey } from "../../utils/constants";
import { cartUpdateEventDL } from "../../utils/dataLayerManager";

export const configQueries = async (
  loading: any,
  setLoading: (loading: any) => void,
  setCart: (res: CartState) => void
) => {
  let temploading = { ...loading };
  // Get Cart Data
  await cartService
    .checkisUserAuth(getCartId() || "")
    .then(async (resp: any) => {
      if (resp.length > 0) {
        await cartService.setCartData(resp, setCart, () => {
          temploading.cart = false;
          setLoading(temploading);
        });
      } else {
        await cartService.createEmptyCart().then(async (res: any) => {
          if (res) {
            await cartService.setCartData(res.cartId, setCart, () => {
              temploading.cart = false;
              setLoading(temploading);
            });
            setCartId(res.cartId);
          }
        });
      }
    })
    .catch(async (e: Error) => {
      await cartService.createEmptyCart().then(async (res: any) => {
        if (res) {
          await cartService.setCartData(res.cartId, setCart, () => {
            temploading.cart = false;
            setLoading(temploading);
          });
          setCartId(res.cartId);
        }
      });
    });
};

const findByUid = (obj: any, categoryName: string) => {
  let stack = [obj];
  while (stack.length > 0) {
    let node = stack.pop();
    for (let key in node) {
      if (node[key] && typeof node[key] === "object") {
        if (node[key]?.name === categoryName) {
          return node[key];
        } else {
          stack.push(node[key]);
        }
      }
    }
  }
  return null;
};

export const packageQueries = async (
  loading: any,
  setLoading: (loading: any) => void,
  setBundlePackages: (res: PackageState) => void,
  setVirtualPackages: (res: PackageState) => void,
  setCategory: (res: CategoryItemState) => void
) => {
  let temploading = { ...loading };

  // Get Category Data
  await categoryInfoService
    .fetchRootCategoryId()
    .then(async (res: any) => {
      if (res) {
        setCategory(res);
        let bundleProductId = "";
        let virtualProductId = "";
        if (res && res.items && res.items.length > 0) {
          res.items.forEach((item: CategoryState) => {
            bundleProductId = findByUid(item, productKey.bundle_products_category_name)?.uid; // need to check
            virtualProductId = findByUid(item, productKey.addon_category_name)?.uid; // need to check
          });
        }

        await packageService.setPackages(
          bundleProductId,
          setBundlePackages,
          () => {
            temploading.bundlePackage = false;
            setLoading(temploading);
          }
        );
        await packageService.setPackages(
          virtualProductId,
          setVirtualPackages,
          () => {
            temploading.virtualPackage = false;
            setLoading(temploading);
          }
        );
      }
      temploading.categoryInfo = false;
      setLoading(temploading);
    })
    .catch((e: Error) => {
      // add error handling
    });
};

const generateAddBundleToCartParams = (
  item: ItemState,
  cartData: CartState,
  attorneySize: number,
  subscriptionType: string,
  contractLength: string
) => {
  let bundleOptions: any =
    item?.items?.map((option) => {
      let value: any = option.options.map((val) => val.id);
      return {
        id: option.option_id,
        quantity: 1, // need to check
        value,
      };
    }) || [];

  const customOptions = (options: Array<CustomizableDropDownOption>) => {
    let customizableOptions: any = [];
    const option = options?.find(
      (data) =>
        data.title === subscriptionType || data.title.includes(contractLength)
    );
    if (option)
      customizableOptions.push({
        id: option.option_id,
        value_string: option.drop_down_option[attorneySize - 1]?.option_type_id,
      });
    return customizableOptions;
  };

  const isMonthlyContract = contractLength.toLowerCase().includes("month");

  let subsIndex =
    item?.options?.findIndex(
      (option) =>
        option.title === subscriptionType ||
        option.title.includes(contractLength)
    ) || 0;
  let params = {
    cartId: cartData.id,
    attorneySize: attorneySize.toString(),
    packageType:
      item?.options?.[subsIndex]?.drop_down_option?.[
        attorneySize - 1
      ]?.price_type?.toLowerCase() ?? "",
    subscriptionType: isMonthlyContract ? "MONTHLY" : "YEARLY",
    sku: item?.sku,
    bundleOptions,
    customizableOptions: item?.options ? customOptions(item.options) : [],
    subscriptionMonth: isMonthlyContract ? contractLength.split("-")[0] : null,
    subscriptionYear: isMonthlyContract ? null : contractLength.split("-")[0],
  };
  return params;
};

export const addBundleItemsToCart = async (
  cartData: CartState,
  selectBundleProduct: string,
  bundlePackages: Array<ItemState>,
  attorneySize: number,
  subscriptionType: string,
  setCart: (res: any) => void,
  setNotification: (res: any) => void,
  contractLength: string
) => {
  let bundleparams: AddBundleItemState = {} as AddBundleItemState;
  let bundleitem = bundlePackages.findIndex(
    (bundle) => bundle.sku === selectBundleProduct
  );
  bundleparams = generateAddBundleToCartParams(
    bundlePackages[bundleitem],
    cartData,
    attorneySize,
    subscriptionType,
    contractLength
  );
  return cartService
    .addBundleItemsToCart(bundleparams)
    .then((res: any) => {
      if (res) {
        addDataLayer(cartUpdateEventDL("add_to_cart", res), "add_to_cart");
        setCart({ ...cartData, ...res });
      }
    })
    .catch((e: Error) => {
      setNotification({
        message: e.message,
        type: "error",
        messageId: generateSecureRandomId(),
      });
      loadDevMessages();
      loadErrorMessages();
    });
};

export const removeAllItemFromCart = async (
  cartData: CartState,
  setCart: (res: CartState) => void,
  setNotification: (res: any) => void
) => {
  const res = await cartService.removeAllCartItems({ cartId: cartData.id });
  if (res && res.id) {
    addDataLayer(cartUpdateEventDL("remove_from_cart", res), "remove_from_cart");
    setCart({ ...cartData, ...res });
    return res.total_quantity;
  } else {
    setNotification({
      message: "Something went wrong",
      type: "error",
      messageId: generateSecureRandomId(),
    });
    loadDevMessages();
    loadErrorMessages();
  }
};

export const removeItemFromCart = async (
  itemId: string,
  cartData: CartState,
  setCart: (res: CartState) => void,
  setNotification: (res: any) => void
) => {
  const res = await cartService.removeCartItems({
    cartId: cartData.id,
    cartItemUid: itemId,
  });
  if (res && res.id) {
    addDataLayer(cartUpdateEventDL("remove_from_cart", res), "remove_from_cart");
    setCart({ ...cartData, ...res });
    return res.total_quantity;
  } else {
    setNotification({
      message: "Something went wrong",
      type: "error",
      messageId: generateSecureRandomId(),
    });
    loadDevMessages();
    loadErrorMessages();
  }
};

const generateAddVirtualToCartParams = (
  items: Array<ItemState>,
  cartData: CartState,
  attorneySize: number,
  subscriptionType: string,
  contractLength: string
) => {
  let cartItems: any = [];
  let customizable_options: any = [];
  const customOptions = (options: Array<CustomizableDropDownOption>) => {
    const option = options?.find(
      (data) =>
        data.title === subscriptionType || data.title.includes(contractLength)
    );
    if (option)
      customizable_options.push({
        id: option.option_id,
        value_string: option.drop_down_option[attorneySize - 1]?.option_type_id,
      });
  };
  items.forEach((item) => {
    if (item?.options) {
      customOptions(item.options);
    }
    cartItems.push({
      data: {
        sku: item.sku,
        quantity: 1,
      },
      customizable_options,
    });
  });
  let subsIndex =
    items[0]?.options?.findIndex(
      (option) =>
        option.title === subscriptionType ||
        option.title.includes(contractLength)
    ) || 0;

  const isMonthlyContract = contractLength.toLowerCase().includes("month");

  return {
    cartId: cartData.id,
    attorneySize: attorneySize.toString(),
    packageType:
      items[0]?.options?.[subsIndex]?.drop_down_option?.[
        attorneySize - 1
      ]?.price_type?.toLowerCase(),
    subscriptionType: isMonthlyContract ? "MONTHLY" : "YEARLY",
    cartItems,
    subscriptionMonth: isMonthlyContract ? contractLength.split("-")[0] : null,
    subscriptionYear: !isMonthlyContract ? contractLength.split("-")[0] : null,
  };
};

export const addVirtualItemsToCart = async (
  cartData: CartState,
  selectedVirtualProducts: Array<ItemState>,
  attorneySize: number,
  subscriptionType: string,
  setCart: (res: any) => void,
  setNotification: (res: any) => void,
  contractLength: string
) => {
  let virtualparams: AddVirtualItemState = {} as AddVirtualItemState;
  const params = generateAddVirtualToCartParams(
    selectedVirtualProducts,
    cartData,
    attorneySize,
    subscriptionType,
    contractLength
  );
  virtualparams = { ...params, packageType: params.packageType ?? "" };
  return cartService
    .addVirtualItemsToCart(virtualparams)
    .then((res: any) => {
      if (res) {
        addDataLayer(cartUpdateEventDL("add_to_cart", res), "add_to_cart");
        setCart({ ...cartData, ...res });
      }
    })
    .catch((e: Error) => {
      setNotification({
        message: e.message,
        type: "error",
        messageId: generateSecureRandomId(),
      });
      loadDevMessages();
      loadErrorMessages();
    });
};

export const createNewEmptyCart = async (
  createEmptyCart: (res: any) => void
) => {
  await cartService
    .createEmptyCart()
    .then((res: any) => {
      if (res) {
        createEmptyCart(res);
        setCartId(res.cartId);
      }
    })
    .catch((e: Error) => {
      loadDevMessages();
      loadErrorMessages();
    });
};
